import React, { useContext, useState } from "react";
import { Container, Table, Button, Image, CloseButton, Modal } from "react-bootstrap";
import { FavoriteContext } from "../context/FavoriteContext";
import { CartContext } from "../context/CartContext";
import { Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import ProductDetailModal from "../components/ProductModalDetail";
import { useNavigate } from "react-router-dom";

const Favorites = () => {
  const { t } = useTranslation();
  const { favorites, removeFavorite } = useContext(FavoriteContext);
  const { cart, addToCart } = useContext(CartContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const storedUser = localStorage.getItem("token");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const navigate = useNavigate();

  // Calculate discounted price
  const calculateDiscountedPrice = (price, discount) => {
    if (discount > 0) {
      return price * (1 - discount / 100);
    }
    return price;
  };

  const handleShowModal = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  // Check if product is already in cart
  const isProductInCart = (productId) => {
    return cart.some((cartItem) => cartItem.product._id === productId);
  };

  // Handle Add to Cart with optional removal from favorites
  const handleAddToCart = (product) => {
    if (!storedUser) {
      setShowLoginModal(true); // ✅ show modal
      return;
    }
    if (isProductInCart(product._id)) {
      Swal.fire({
        icon: "info",
        title: t("already_in_cart_title"),
        text: t("already_in_cart_text", { product: product.title }),
        showConfirmButton: true,
        confirmButtonText: t("go_to_cart"),
        showCancelButton: true,
        cancelButtonText: t('Continue Shopping')
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/cart");
        }
      });
    } else {
      addToCart(product);
      Swal.fire({
        icon: "success",
        title: "Added to Cart",
        text: `${product.title} has been added to your cart`,
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  // Handle modal add to cart (for product detail modal)
  const handleModalAddToCart = (product) => {
    if (!storedUser) {
      setShowLoginModal(true);
      handleCloseModal(); // Close product detail modal
      return;
    }
    handleAddToCart(product);
  };

  // Render empty state
  if (favorites.length === 0) {
    return (
      <Container className="cart-mt text-center" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
        <h3> {t('Your favourite is empty')} </h3>
        <Link to="/men-perfume" className="btn btn-primary btn-color btn-card-hover border-0 px-4 py-2 mt-5">
          {t('Continue Shopping')}
        </Link>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 favourite-page" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      <Link
        to="/shop"
        className="d-flex mt-fav w-100 align-items-center mb-4 back-link"
      >
        <FaArrowLeft className="me-2 back-icon text-dark" />
        <h5 className="mb-0 ms-2">{t('BACK TO SHOP')}</h5>
      </Link>
      <Table bordered hover responsive className="mt-4 favorites-table">
        <tbody>
          {favorites.map((product) => (
            <tr key={product._id}>
              <td data-label="Product" onClick={() => handleShowModal(product)} style={{ cursor: "pointer" }}>
                <div style={{ position: "relative" }}>
                  <Image
                    src={product.image}
                    className="fav-image"
                  />
                  {product.discount > 0 && (
                    <span className="position-absolute top-0 start-0 bg-danger text-white px-2 py-1 rounded" style={{ fontSize: "0.7rem" }}>
                      -{product.discount}%
                    </span>
                  )}
                </div>
              </td>

              <td data-label="Title" className="ps-4" style={{ cursor: "pointer" }} onClick={() => handleShowModal(product)}>
                {product.title}
              </td>
              <td data-label="Price" className="text-center" style={{ cursor: "pointer" }} onClick={() => handleShowModal(product)}>
                {product.discount > 0 ? (
                  <div className="d-flex justify-content-center align-items-center gap-2">
                    <span className="text-decoration-line-through text-muted me-2">
                      ${product.price.toFixed(2)}
                    </span>
                    <span className="primary-color">
                      ${calculateDiscountedPrice(product.price, product.discount).toFixed(2)}
                    </span>
                  </div>
                ) : (
                  <span className="primary-color text-end">
                    ${product.price.toFixed(2)}
                  </span>
                )}
              </td>

              <td data-label="Actions" className="text-center">
                <div className="d-flex gap-2 justify-content-evenly flex-wrap">
                  <Button
                    variant=""
                    className={`btn-color btn-card-hover border-0 text-white py-2 fav-button-size fw-medium ${isProductInCart(product._id) ? "btn-secondary" : ""
                      }`}
                    size="sm"
                    onClick={() => handleAddToCart(product)}
                  >
                    {isProductInCart(product._id) ? t('in_cart') : t('add_to_cart')}
                  </Button>
                  <CloseButton
                    className="float-end"
                    variant="danger"
                    onClick={() => removeFavorite(product._id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          show={showModal}
          onHide={handleCloseModal}
          product={selectedProduct}
          onAddToCart={handleModalAddToCart} // Use the modified handler
        />
      )}

      {/* Required login */}
      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
        <Modal.Body className="shadow-lg rounded-3">
          <CloseButton
            className="close-btn p-0 float-end"
            onClick={() => setShowLoginModal(false)}
          />

          <div className="d-flex justify-content-between align-items-center mt-3">
            <p className="pt-3">Login is required!</p>
            <Button
              variant=""
              className="bg-transparent border px-3 login-hover"
              onClick={() => {
                setShowLoginModal(false);
                navigate("/login");
              }}
            >
              Log In
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Favorites;
